/**
 * Measured recording metrics.
 *
 * THE RULE THIS MODULE EXISTS TO ENFORCE: a requested bitrate is not an
 * achieved bitrate, and `MediaStreamTrack.getSettings()` does not prove one.
 * `getSettings()` describes the TRACK feeding the encoder — width, height,
 * frame rate — not the encoder's output. The only honest measure of bitrate is
 * the file that came out:
 *
 *     measured = file_size_bytes * 8 / duration_seconds
 *
 * Validated against a real browser recording: 500 kbps was requested and
 * 104 kbps was produced. Anything that treats the request as the result would
 * have been wrong by a factor of five.
 *
 * A second, subtler trap: a byte-concatenated MediaRecorder WebM reports
 * `duration === Infinity` in the browser (confirmed on Chromium 151 with a
 * cleanly stopped recorder), so duration MUST come from a wall clock, never
 * from `HTMLVideoElement.duration`. `durationFromVideoElement` exists only to
 * make that failure explicit.
 */
/** How far above the expected bitrate a result may land before we complain. */
export const BITRATE_PASS_MULTIPLIER = 1.25;
export const BITRATE_WARN_MULTIPLIER = 1.5;
/**
 * Bytes-and-seconds bitrate, in bits per second.
 *
 * Returns 0 for a non-positive duration rather than Infinity: callers render
 * this, and an Infinity in a UI is worse than an obvious zero.
 */
export const measuredBitrateBps = (sizeBytes, durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0)
        return 0;
    if (!Number.isFinite(sizeBytes) || sizeBytes <= 0)
        return 0;
    return Math.round((sizeBytes * 8) / durationSec);
};
/**
 * Classify a measured bitrate against what the configuration asked for.
 *
 * VBR means a well-behaved encoder still varies, so `pass` is a band and not an
 * equality. `fail` means the browser is substantively disregarding the request
 * — which is a real possibility (the spec makes `videoBitsPerSecond` a hint),
 * and is precisely why this is measured and surfaced rather than assumed.
 */
export const classifyBitrate = (measuredBps, expectedBps) => {
    if (!Number.isFinite(expectedBps) || expectedBps <= 0)
        return 'pass';
    if (measuredBps <= expectedBps * BITRATE_PASS_MULTIPLIER)
        return 'pass';
    if (measuredBps <= expectedBps * BITRATE_WARN_MULTIPLIER)
        return 'warn';
    return 'fail';
};
/** Linear extrapolation. ALWAYS present this to a human as an estimate: screen
 *  content dominates, and a static editor compresses very differently from a
 *  candidate scrolling a diff. */
export const estimateSizeBytes = (measuredBps, seconds) => Math.round((measuredBps * seconds) / 8);
export const THIRTY_MINUTES_SEC = 30 * 60;
/**
 * Build the measurement a bench or a telemetry payload reports.
 *
 * `actual` carries track settings because they ARE reliable for geometry. They
 * are deliberately kept in a separate field from the bitrate so no caller can
 * mistake one for the other.
 *
 * NOTE ON THE VIDEO/AUDIO SPLIT: it is not measurable here, and not by ffprobe
 * either — probing a real browser WebM returned per-stream `bit_rate=N/A` both
 * before and after a remux. Only the container total is observable. Callers
 * must label the split as "requested".
 */
export const measure = (sizeBytes, durationSec, expectedBitrateBps, actual = {}) => {
    const bitrate = measuredBitrateBps(sizeBytes, durationSec);
    return {
        durationSec,
        sizeBytes,
        measuredBitrateBps: bitrate,
        expectedBitrateBps,
        band: classifyBitrate(bitrate, expectedBitrateBps),
        thirtyMinuteEstimateBytes: estimateSizeBytes(bitrate, THIRTY_MINUTES_SEC),
        actual,
    };
};
/**
 * Read a duration from a played-back blob, or `null` when it is unusable.
 *
 * Exists to make the Infinity case explicit at the call site. A concatenated
 * MediaRecorder WebM reports `Infinity` here, and `duration || 0` — the idiom
 * used in `ProctoringVideoOverlay` — passes Infinity straight through, because
 * Infinity is truthy. Any caller wanting a real number must fall back to a
 * wall clock.
 */
export const usableDuration = (raw) => typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
/** How long to wait for the browser to resolve a duration before giving up. */
export const DURATION_RECOVERY_TIMEOUT_MS = 15000;
/**
 * A seek target past the end of any conceivable media.
 *
 * Seeking beyond the end is what makes the browser resolve the real duration:
 * it cannot clamp the seek without knowing where the end is, so it works the
 * length out and fires `durationchange`. 1e101 is used rather than `Infinity`
 * because assigning Infinity to `currentTime` throws in some engines.
 */
const SEEK_PAST_END = 1e101;
/**
 * Recover a usable duration from media whose container does not declare one.
 *
 * WHY THIS EXISTS — and why it is not a hack looking for a home.
 *
 * `MediaRecorder` started WITH a timeslice writes its WebM header in streaming
 * mode: the Segment Duration is left unknown, because the length genuinely is
 * not known at the moment the header is emitted, and it is never rewritten.
 * Every recording this platform produces is therefore a valid, fully decodable
 * WebM that reports `duration === Infinity`.
 *
 * That was previously "fixed" on the server with `ffmpeg -fflags +genpts
 * -c copy`, which rewrote the header for the cost of ~252 bytes — an entire
 * download/remux/re-upload stage to edit a few bytes of metadata. Measured in
 * Chromium 151, this recovers the same duration in tens of milliseconds without
 * touching a single byte of video:
 *
 *     duration on load        Infinity
 *     after recovery          11.892s   (wall clock ~11.9s)
 *     seeks at 25/50/75%      all land exactly on target
 *     seekable.end(0)         becomes finite
 *
 * The recovery is a scan, so its cost grows with file size (~55 ms/MB measured
 * over 0.09MB->8.14MB). That is the reason the admin bitrate ceiling matters to
 * playback and not only to bandwidth.
 *
 * NEVER write `video.duration || 0` against this media: `Infinity` is truthy,
 * so the guard passes Infinity straight through and every progress calculation
 * downstream silently becomes 0. That specific bug is what broke the scrub bar.
 *
 * Resolves with a finite, positive duration, or `null` if it could not be
 * determined. Never throws, and never leaves a listener attached.
 */
export const recoverMediaDuration = (video, timeoutMs = DURATION_RECOVERY_TIMEOUT_MS) => {
    const already = usableDuration(video.duration);
    if (already !== null)
        return Promise.resolve(already);
    return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const cleanup = () => {
            video.removeEventListener('durationchange', onDurationChange);
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
        };
        // Every exit runs through here, so a listener can never outlive the call —
        // these players are mounted and unmounted repeatedly in the report UI.
        const finish = (value) => {
            if (settled)
                return;
            settled = true;
            cleanup();
            resolve(value);
        };
        function onDurationChange() {
            const found = usableDuration(video.duration);
            if (found !== null)
                finish(found);
        }
        video.addEventListener('durationchange', onDurationChange);
        timer = setTimeout(() => finish(null), timeoutMs);
        try {
            video.currentTime = SEEK_PAST_END;
        }
        catch (_a) {
            // Some engines reject an out-of-range seek outright. Nothing further to
            // try; the timeout resolves null and the caller degrades gracefully.
            finish(null);
        }
    });
};
/**
 * Return the media to a sane position after recovery.
 *
 * The recovery seek leaves `currentTime` at the end of the media, which would
 * show the reviewer the last frame of an interview they have not watched yet.
 */
export const rewindAfterRecovery = (video) => {
    try {
        video.currentTime = 0;
    }
    catch (_a) {
        /* a detached or errored element cannot be rewound, and need not be */
    }
};
